import distance from "@turf/distance";
import { Feature, Point } from "geojson";
import { GeoJSONSource, MapMouseEvent } from "mapbox-gl";
import React, { useState } from "react";
import { MeasurementSystem } from "velavu-js-api";
import {
	MapHelperInstance,
	useListenerSetupMapEffect,
	useStyleDependantMapEffect,
	useStyleSetupMapEffect,
} from "../../helper/map-helper";
import { formatMeasuredDistance } from "../../helper/unit-helper";
import * as globals from "../../styles/global.icss.scss";
import whitelabel from "../../whitelabel";
import { geoJSONBlankCollection } from "./mapbox-injectable";

const mapboxIDMeasureLine = "measure-distance-line";
const mapboxIDMeasureLabel = "measure-distance-label";
const mapboxIDMeasurePoints = "measure-distance-points";

export interface MeasureDistanceLine {
	start: [number, number];
	// Null while the endpoint is still being placed.
	end: [number, number] | null;
}

export interface MapboxInjectableMeasureDistanceParams {
	mapInstance: MapHelperInstance;
	line: MeasureDistanceLine | null;
	onChangeLine: React.Dispatch<
		React.SetStateAction<MeasureDistanceLine | null>
	>;
	measurementSystem: MeasurementSystem;
}

/**
 * Returns whether the endpoint of a measure line is still being placed
 */
export function isMeasureDistanceLinePlacing(
	line: MeasureDistanceLine | null,
): boolean {
	return line !== null && line.end === null;
}

/**
 * Returns the rotation of text along a line segment
 */
function measureLabelBearing(
	start: [number, number],
	end: [number, number],
): number {
	const lat1 = (start[1] * Math.PI) / 180;
	const lat2 = (end[1] * Math.PI) / 180;
	const dLng = ((end[0] - start[0]) * Math.PI) / 180;
	const y = Math.sin(dLng) * Math.cos(lat2);
	const x =
		Math.cos(lat1) * Math.sin(lat2) -
		Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
	if (x === 0 && y === 0) {
		return 0;
	}

	// Geographic bearing is clockwise from north.
	let rotation = (Math.atan2(y, x) * 180) / Math.PI - 90;
	rotation = ((((rotation + 180) % 360) + 360) % 360) - 180;
	if (rotation > 90 || rotation < -90) {
		rotation += rotation > 0 ? -180 : 180;
	}
	return rotation;
}

type MeasurePointEndpoint = "start" | "end";

interface MeasurePointProperties {
	endpoint: MeasurePointEndpoint;
}

export default function useMapboxInjectableMeasureDistance(
	params: MapboxInjectableMeasureDistanceParams,
) {
	//Create the map layers
	useStyleSetupMapEffect(params.mapInstance, (_, box) => {
		box.addSource(mapboxIDMeasureLine, {
			type: "geojson",
			data: geoJSONBlankCollection,
		});
		box.addLayer({
			id: mapboxIDMeasureLine,
			type: "line",
			source: mapboxIDMeasureLine,
			layout: {
				"line-join": "round",
				"line-cap": "round",
			},
			paint: {
				"line-color": globals.accent,
				"line-width": 3,
				"line-opacity": ["case", ["get", "placing"], 0.5, 1],
			},
		});

		box.addSource(mapboxIDMeasureLabel, {
			type: "geojson",
			data: geoJSONBlankCollection,
		});
		box.addLayer({
			id: mapboxIDMeasureLabel,
			type: "symbol",
			source: mapboxIDMeasureLabel,
			layout: {
				"symbol-placement": "point",
				"text-field": ["get", "label"],
				"text-size": 14,
				"text-offset": [0, 1.2],
				"text-rotate": ["get", "bearing"],
				"text-rotation-alignment": "map",
				"text-justify": "center",
				"text-allow-overlap": true,
				"text-ignore-placement": true,
				"text-font": [
					"Poppins",
					"Open Sans Regular",
					"Arial Unicode MS Regular",
				],
			},
			paint: {
				"text-color": whitelabel.palette.primary900,
				"text-halo-color": whitelabel.palette.secondary50,
				"text-halo-width": 1,
			},
		});

		box.addSource(mapboxIDMeasurePoints, {
			type: "geojson",
			data: geoJSONBlankCollection,
		});
		box.addLayer({
			id: mapboxIDMeasurePoints,
			type: "circle",
			source: mapboxIDMeasurePoints,
			paint: {
				"circle-color": whitelabel.palette.secondary50,
				"circle-stroke-color": globals.accent,
				"circle-stroke-width": 2,
				"circle-radius": 6,
			},
		});
	});

	const isPlaced = Boolean(params.line?.end);

	//Track the line end cursor
	const [lineEndCursor, setLineEndCursor] = useState<[number, number] | null>(
		null,
	);
	useListenerSetupMapEffect(
		params.mapInstance.map,
		(_, box) => {
			box.on("mousemove", (event) => {
				setLineEndCursor([event.lngLat.lng, event.lngLat.lat]);
			});
		},
		[setLineEndCursor],
	);

	//Update the map layers
	useStyleDependantMapEffect(
		params.mapInstance,
		(map) => {
			const lineSource = map.getSource(
				mapboxIDMeasureLine,
			) as GeoJSONSource;
			const labelSource = map.getSource(
				mapboxIDMeasureLabel,
			) as GeoJSONSource;
			const pointsSource = map.getSource(
				mapboxIDMeasurePoints,
			) as GeoJSONSource;

			//Render nothing if we have no line
			const line = params.line;
			if (line === null) {
				lineSource.setData(geoJSONBlankCollection);
				pointsSource.setData(geoJSONBlankCollection);
				labelSource.setData(geoJSONBlankCollection);
				return;
			}

			//Set the points data
			const points: Feature<Point, MeasurePointProperties>[] = [
				{
					type: "Feature",
					geometry: {
						type: "Point",
						coordinates: line.start,
					},
					properties: {
						endpoint: "start",
					},
				},
			];

			const end = line.end ?? lineEndCursor;
			if (end !== null) {
				points.push({
					type: "Feature",
					geometry: {
						type: "Point",
						coordinates: end,
					},
					properties: {
						endpoint: "end",
					},
				});
			}
			pointsSource.setData({
				type: "FeatureCollection",
				features: points,
			});

			//Set the line and its label
			if (end !== null) {
				lineSource.setData({
					type: "Feature",
					geometry: {
						type: "LineString",
						coordinates: [line.start, end],
					},
					properties: {
						placing: line.end === null,
					},
				});

				const meters = distance(line.start, end, { units: "meters" });
				labelSource.setData({
					type: "Feature",
					geometry: {
						type: "Point",
						coordinates: [
							(line.start[0] + end[0]) / 2,
							(line.start[1] + end[1]) / 2,
						],
					},
					properties: {
						label: formatMeasuredDistance(
							meters,
							params.measurementSystem,
						),
						bearing: measureLabelBearing(line.start, end),
					},
				});
			} else {
				lineSource.setData(geoJSONBlankCollection);
			}
		},
		[params.line, lineEndCursor, params.measurementSystem],
	);

	//Handle clicking to set the end point
	useListenerSetupMapEffect(
		params.mapInstance.map,
		(map, box) => {
			if (!isMeasureDistanceLinePlacing(params.line)) {
				return;
			}

			map.getCanvas().style.cursor = "crosshair";

			//Set the line end point when we click to finish the line
			box.on("click", (event) => {
				const end: [number, number] = [
					event.lngLat.lng,
					event.lngLat.lat,
				];
				params.onChangeLine((current) => {
					if (current === null || current.end !== null) {
						return current;
					}
					return {
						start: current.start,
						end: end,
					};
				});
			});

			//Discard the line when we press escape
			const onKeyDown = (event: KeyboardEvent) => {
				if (event.key === "Escape") {
					params.onChangeLine(null);
				}
			};
			document.addEventListener("keydown", onKeyDown);

			return () => {
				document.removeEventListener("keydown", onKeyDown);
				map.getCanvas().style.cursor = "";
			};
		},
		[params.line, params.onChangeLine],
	);

	useListenerSetupMapEffect(
		params.mapInstance.map,
		(map, box) => {
			//Ignore if we have no line placed
			if (!isPlaced) {
				return;
			}

			const canvas = map.getCanvas();

			interface MoveTarget {
				endpoint: MeasurePointEndpoint;
				origin: { x: number; y: number };
			}
			let moveTarget: MoveTarget | null = null;
			let moved = false;

			/**
			 * Sets the endpoint of the active line
			 * @param endpoint The endpoint to set
			 * @param coords The coordinates to set
			 */
			function updateEndpoint(
				endpoint: MeasurePointEndpoint,
				coords: [number, number],
			) {
				params.onChangeLine((current) => {
					if (current === null || current.end === null) {
						return current;
					}
					switch (endpoint) {
						case "start":
							return {
								start: coords,
								end: current.end,
							};
						case "end":
							return {
								start: current.start,
								end: coords,
							};
					}
				});
			}

			const onMove = (event: MapMouseEvent) => {
				//Ignore if we have no endpoint we're moving
				if (moveTarget === null) {
					return;
				}

				//Resolve how far we moved the point from its original position
				const dx = event.point.x - moveTarget.origin.x;
				const dy = event.point.y - moveTarget.origin.y;

				//Update the line
				moved = true;
				updateEndpoint(moveTarget.endpoint, [
					event.lngLat.lng,
					event.lngLat.lat,
				]);
			};

			const onUp = (event: MapMouseEvent) => {
				//Ignore if we have no move target
				if (moveTarget === null) {
					return;
				}

				//Remove the mouse move listener
				map.off("mousemove", onMove);

				//If we didn't move the point, discard the line
				if (!moved) {
					params.onChangeLine(null);
					canvas.style.cursor = "";
				} else {
					//If we moved the point, update the endpoint
					updateEndpoint(moveTarget.endpoint, [
						event.lngLat.lng,
						event.lngLat.lat,
					]);
					canvas.style.cursor = "pointer";
				}

				moveTarget = null;
				moved = false;
			};

			//Listen for clicks on the points to start moving them
			box.on(
				"mousedown",
				mapboxIDMeasurePoints,
				(event: MapMouseEvent) => {
					event.preventDefault();

					//Retrieve the properties of the point we clicked on
					const properties = event.features?.[0]
						?.properties as MeasurePointProperties;

					//Set the move target
					moveTarget = {
						endpoint: properties.endpoint,
						origin: { x: event.point.x, y: event.point.y },
					};
					moved = false;

					//Update the cursor style
					canvas.style.cursor = "grabbing";

					//Set the map event listeners
					map.on("mousemove", onMove);
					map.once("mouseup", onUp);
				},
			);

			//Update the cursor style
			box.on("mouseenter", mapboxIDMeasurePoints, () => {
				if (moveTarget === null) {
					canvas.style.cursor = "pointer";
				}
			});
			box.on("mouseleave", mapboxIDMeasurePoints, () => {
				if (moveTarget === null) {
					canvas.style.cursor = "";
				}
			});

			//Clean up
			return () => {
				map.off("mousemove", onMove);
				map.off("mouseup", onUp);
				canvas.style.cursor = "";
			};
		},
		[isPlaced, params.onChangeLine],
	);
}

export interface MeasureDistanceControlState {
	line: MeasureDistanceLine | null;
	start: (origin: [number, number]) => void;
	clear: () => void;
}
